import type { UserRole } from '@/hooks/useUserRole';

/**
 * SECURITY: Centralized routing logic to prevent hardcoded navigation and ensure consistent access control
 * Compliance: SOC 2 (CC6.1), OWASP A01:2021 - Broken Access Control
 */

/**
 * Allowed internal paths for return_to parameter
 * SECURITY: Prevents open redirect attacks (OWASP A01:2021)
 */
const ALLOWED_RETURN_PATHS = [
  '/profile',
  '/org',
  '/admin',
  '/settings',
  '/account',
  '/form-invitation',
  '/apply',
];

/**
 * Determines the home route for a user based on their roles and organization context
 * 
 * @param roles - Array of user roles from user_roles table
 * @param organizationId - Optional organization ID from primaryRole
 * @param lastOrgId - Optional last accessed organization ID from localStorage/state
 * @returns The appropriate home route path
 * 
 * Priority order:
 * 1. admin → /admin/overview
 * 2. organization_admin → /org/dashboard
 * 3. parent → /profile/dashboard
 * 4. guest → /apply (form applicants before full registration)
 * 5. No role → /account-type-selection
 */
export function homeRouteFor(
  roles?: UserRole[] | null,
  organizationId?: string | null,
  lastOrgId?: string | null
): string {
  if (!roles || roles.length === 0) {
    // SAFETY CHECK: If user has pending org signup but no roles yet,
    // redirect back to callback to complete initialization
    if (typeof window !== 'undefined') {
      const pendingOrgData = localStorage.getItem('pending_org_signup');
      const timestamp = localStorage.getItem('pending_org_signup_timestamp');
      
      if (pendingOrgData && timestamp) {
        const age = Date.now() - parseInt(timestamp);
        const TEN_MINUTES = 10 * 60 * 1000;
        
        if (age < TEN_MINUTES) {
          console.debug('[homeRouteFor] Pending org signup detected with no roles - redirecting to callback');
          return '/auth/callback?type=org_signup';
        }
      }
    }
    
    return '/account-type-selection';
  }

  // SAFETY CHECK: If user only has parent role but has pending org signup,
  // the org initialization may have failed - redirect to retry
  const onlyHasParentRole = roles.length === 1 && roles[0].role === 'parent';
  if (onlyHasParentRole && typeof window !== 'undefined') {
    const pendingOrgData = localStorage.getItem('pending_org_signup');
    const timestamp = localStorage.getItem('pending_org_signup_timestamp');
    
    if (pendingOrgData && timestamp) {
      const age = Date.now() - parseInt(timestamp);
      const TEN_MINUTES = 10 * 60 * 1000;
      
      if (age < TEN_MINUTES) {
        console.debug('[homeRouteFor] Parent role with pending org signup - redirecting to callback for retry');
        return '/auth/callback?type=org_signup';
      }
    }
  }

  // Check for platform admin role (highest priority)
  const hasPlatformAdminRole = roles.some((r) => r.role === 'admin');
  if (hasPlatformAdminRole) {
    return '/admin/overview';
  }

  // Check for organization admin
  const hasOrgAdminRole = roles.some((r) => r.role === 'organization_admin');
  if (hasOrgAdminRole) {
    return '/org/dashboard';
  }

  // Check for parent role (also handle legacy 'user' role)
  const hasParentRole = roles.some((r) => r.role === 'parent' || r.role === 'user');
  if (hasParentRole) {
    return '/profile/dashboard';
  }

  // Check for guest role (form applicants before full registration)
  const hasGuestRole = roles.some((r) => r.role === 'guest');
  if (hasGuestRole) {
    return '/apply';
  }

  // No recognized role
  return '/account-type-selection';
}

/**
 * Safely parses and validates a return_to URL parameter
 * 
 * SECURITY: Prevents open redirect attacks by validating against allowlist
 * 
 * @param searchParams - URLSearchParams object from useSearchParams or window.location.search
 * @param fallback - Fallback route if return_to is invalid or missing
 * @returns Valid internal path or fallback
 */
export function parseReturnTo(searchParams: URLSearchParams, fallback: string): string {
  const returnTo = searchParams.get('return_to');

  if (!returnTo) {
    return fallback;
  }

  // SECURITY: Block external URLs to prevent open redirect
  if (returnTo.startsWith('http://') || returnTo.startsWith('https://') || returnTo.startsWith('//')) {
    console.warn('[Security] Blocked external return_to URL:', returnTo);
    return fallback;
  }

  const ensureLeadingSlash = (path: string) => (path.startsWith('/') ? path : `/${path}`);
  const stripTrailingSlashes = (path: string) => path.replace(/\/+$/, '') || '/';

  // Ensure path starts with /
  const normalizedPath = stripTrailingSlashes(ensureLeadingSlash(returnTo));

  // SECURITY: Validate against allowlist to prevent redirect to unintended pages
  const isAllowed = ALLOWED_RETURN_PATHS.some((allowedPath) => {
    const normalizedAllowed = stripTrailingSlashes(allowedPath);
    return normalizedPath === normalizedAllowed || normalizedPath.startsWith(`${normalizedAllowed}/`);
  });

  if (!isAllowed) {
    console.warn('[Security] Blocked disallowed return_to path:', normalizedPath);
    return fallback;
  }

  return normalizedPath;
}

/**
 * Builds a URL with return_to query parameter
 * 
 * @param basePath - Base path for the URL (e.g., '/login', '/settings/security/mfa-setup')
 * @param returnTo - Optional return path to append as query param
 * @returns Complete URL with query string if returnTo is provided
 * 
 * @example
 * buildReturnToUrl('/login', '/profile/vault') → '/login?return_to=/profile/vault'
 * buildReturnToUrl('/login') → '/login'
 */
export function buildReturnToUrl(basePath: string, returnTo?: string): string {
  if (!returnTo) {
    return basePath;
  }

  const url = new URL(basePath, window.location.origin);
  url.searchParams.set('return_to', returnTo);
  return `${url.pathname}${url.search}`;
}
